<template>
  <div class="user-view">
    <div class="profile-header">
      <div class="avatar-section">
        <div class="avatar-ring">
          <van-image
              round
              width="100px"
              height="100px"
              :src="imageUrl(user.avatar)"
              class="profile-avatar"
          />
        </div>
        <div class="user-id-badge" @click="copyText(user.userId)">
          <span>UID: {{ user.userId }}</span>
          <van-icon name="copy-o"/>
        </div>
      </div>

      <div class="balance-card">
        <div class="balance-label">Total Balance</div>
        <div class="balance-amount">
          <span class="currency-symbol">{{ user.areaConfig?.currency }}</span>
          {{ user.balance?.balance }}
        </div>
        <div class="balance-sub">
          ≈ $ {{ user.balance?.usdtBalance }} USDT
        </div>
        <div class="deposit-info">
          <van-icon name="gold-coin-o"/>
          Deposit: {{ user.balance?.totalOutputIncome }} USDT
        </div>
      </div>
    </div>

    <div class="action-grid">
      <div class="action-item" @click="handleWithdrawalsRecords">
        <div class="action-icon-wrapper records">
          <van-icon name="notes-o"/>
        </div>
        <span>Assets Records</span>
      </div>
      <div class="action-item" @click="handleWithdrawals">
        <div class="action-icon-wrapper withdraw">
          <van-icon name="card"/>
        </div>
        <span>Withdraw</span>
      </div>
      <div class="action-item" @click="handleSubmitKyc">
        <div class="action-icon-wrapper kyc">
          <van-icon name="idcard"/>
        </div>
        <span>Verify KYC</span>
      </div>
      <div class="action-item" @click="handleResetPwd">
        <div class="action-icon-wrapper password">
          <van-icon name="shield-o"/>
        </div>
        <span>Security</span>
      </div>
    </div>

    <div v-if="autoReceiveLoaded && adminAutoReceiveConfigured" class="settings-card">
      <div class="auto-receive-row">
        <div class="auto-receive-text">
          <div class="auto-receive-title">Sleep</div>
          <p class="auto-receive-desc">
            When Sleep is on, new orders will not be assigned to you automatically. Turn it off to resume auto receive. You can still grab orders manually on Home.
          </p>
        </div>
        <van-switch
            v-model="sleepMode"
            size="22px"
            :loading="autoReceiveUpdating"
            @change="handleSleepModeChange"
        />
      </div>
    </div>

    <div class="menu-list">
      <van-cell title="Contact Tasks" is-link icon="orders-o" @click="handleContactTasks"/>
      <van-cell title="My Task Orders" is-link icon="list" @click="handleContactTaskOrders"/>
      <van-cell title="Support Center" is-link icon="service-o"/>
      <van-cell title="About Us" is-link icon="info-o"/>
      <div class="logout-cell" @click="handleLogout">
        <van-icon name="log-out"/>
        <span>Logout</span>
      </div>
    </div>

    <!-- Withdraw Popup -->
    <van-popup
        v-model:show="withdrawalsVisible"
        round
        position="bottom"
        class="withdraw-popup"
    >
      <div class="popup-header">
        <h3>Withdraw Funds</h3>
        <van-icon name="cross" @click="withdrawalsVisible = false"/>
      </div>

      <div class="popup-body">
        <div class="form-group">
          <label>Withdrawal Method</label>
          <div class="method-selector" @click="showPicker = true">
            <span>{{ result || 'Select Method' }}</span>
            <van-icon name="arrow"/>
          </div>
        </div>

        <van-field
            v-model="withdrawalsData.account"
            label="Account"
            disabled
            placeholder="Account information"
            class="custom-field"
        />

        <van-field
            v-model="withdrawalsData.amount"
            label="Amount"
            :disabled="getCurrency()!=='AED'"
            placeholder="Available balance"
            class="custom-field"
        />

        <div class="upload-section">
          <label>Verification Video</label>
          <van-uploader
              v-model="fileList"
              max-count="1"
              accept="video/*"
              :after-read="afterRead"
              class="custom-uploader"
          >
            <div class="uploader-placeholder">
              <van-icon name="video-o" size="32"/>
              <p>Upload Payment Video</p>
            </div>
          </van-uploader>
        </div>

        <van-button
            type="primary"
            block
            round
            @click="submitWithdrawals"
            class="submit-btn"
        >
          Confirm Withdrawal
        </van-button>
      </div>
    </van-popup>

    <van-popup v-model:show="showPicker" round position="bottom">
      <van-picker
          :columns="getWithdrawTypeOptions()"
          confirm-button-text="Confirm"
          cancel-button-text="Cancel"
          @confirm="onConfirm"
          @cancel="showPicker = false"
      />
    </van-popup>
  </div>
</template>

<script setup>
import {onMounted, ref} from 'vue'
import {showConfirmDialog, showToast} from 'vant'
import {
  applyWithdraw,
  getAutoReceiveSetting,
  getCurrency,
  getProfile,
  logout,
  updateAutoReceiveSetting,
} from '@/utils/api'
import {useRouter} from 'vue-router'
import {imageUrl} from '@/utils'

const router = useRouter()
const withdrawalsVisible = ref(false)
const showPicker = ref(false)
const withdrawOptions = [
  {text: 'BSC20', value: 'BSC20'},
  {text: 'Binance ID', value: 'BINANCE_ID'}
  // {text: 'TRC20', value: 'TRC20'},
]

const withdrawOptions_AED = [
  {text: 'Sell Platform', value: 'INNER_TRANSFER'},
  {text: 'BSC20', value: 'BSC20'},
  {text: 'Binance ID', value: 'BINANCE_ID'},
]

const result = ref('')
const withdrawalsData = ref({
  account: '',
  amount: 0,
  withdrawType: 'BINANCE_ID',
  file: null,
})

const user = ref({
  avatar: '',
  userId: '',
  balance: {balance: 0, usdtBalance: 0, totalOutputIncome: 0},
  areaConfig: {currency: 'SAR'},
})

const autoReceiveLoaded = ref(false)
const sleepMode = ref(false)
const adminAutoReceiveConfigured = ref(false)
const autoReceiveUpdating = ref(false)
let suppressSleepChange = false

const getWithdrawTypeOptions = () => {
  return getCurrency() === 'AED' && user.value.payInUserId
      ? withdrawOptions_AED : withdrawOptions
}

const fetchUserData = async () => {
  try {
    const response = await getProfile()
    user.value = response.data
    localStorage.setItem('currency', user.value.areaConfig?.currency || '')
  } catch (error) {
    console.error('Error fetching user data:', error)
  }
}

const fetchAutoReceiveSetting = async () => {
  try {
    const response = await getAutoReceiveSetting()
    const data = response.data || {}
    sleepMode.value = data.enabled === false
    adminAutoReceiveConfigured.value = !!data.adminConfigured
  } catch (error) {
    console.error('Error fetching auto receive setting:', error)
  } finally {
    autoReceiveLoaded.value = true
  }
}

const handleSleepModeChange = async (sleep) => {
  if (suppressSleepChange) {
    suppressSleepChange = false
    return
  }
  if (autoReceiveUpdating.value) {
    return
  }

  if (sleep) {
    try {
      await showConfirmDialog({
        title: 'Turn On Sleep?',
        message: 'New orders will not be assigned to you automatically. You can still grab orders manually on Home.',
        confirmButtonText: 'Sleep',
        cancelButtonText: 'Cancel',
      })
    } catch {
      suppressSleepChange = true
      sleepMode.value = false
      return
    }
  }

  const previousSleep = !sleep
  autoReceiveUpdating.value = true
  try {
    const response = await updateAutoReceiveSetting({ enabled: !sleep })
    const data = response.data || {}
    suppressSleepChange = true
    sleepMode.value = data.enabled === false
    adminAutoReceiveConfigured.value = !!data.adminConfigured
    showToast(sleep ? 'Sleep mode on' : 'Sleep mode off')
  } catch (error) {
    suppressSleepChange = true
    sleepMode.value = previousSleep
    showToast('Update failed')
    console.error('Error updating sleep setting:', error)
  } finally {
    autoReceiveUpdating.value = false
  }
}

const handleWithdrawals = () => {
  withdrawalsVisible.value = true
  const initialType = getWithdrawTypeOptions()[0]
  result.value = initialType.text
  withdrawalsData.value.withdrawType = initialType.value
  if (initialType.value === 'INNER_TRANSFER') {
    withdrawalsData.value.account = user.value.payInUserId;
  } else {
    withdrawalsData.value.account = user.value.parsedWithdrawInfo?.[initialType.value] || ''
  }
  withdrawalsData.value.amount = user.value.balance?.balance || 0
}

const handleSubmitKyc = () => router.push('/submitkyc')
const handleResetPwd = () => router.push('/ResetPwd')
const handleWithdrawalsRecords = () => router.push('/withdraw-records')
const handleContactTasks = () => router.push('/contact-task')
const handleContactTaskOrders = () => router.push('/contact-task-order')

const handleLogout = async () => {
  try {
    await logout()
    showToast('Logged out')
    localStorage.removeItem('token')
    router.push('/login')
  } catch (error) {
    console.error('Error logging out:', error)
  }
}

const copyText = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    showToast('Copied')
  })
}

const onConfirm = ({selectedOptions}) => {
  showPicker.value = false
  const option = selectedOptions[0]
  result.value = option.text
  withdrawalsData.value.withdrawType = option.value
  withdrawalsData.value.account = user.value.parsedWithdrawInfo?.[option.value] || ''
}

const fileList = ref([])
const afterRead = (file) => {
  withdrawalsData.value.file = file.file
}

const submitWithdrawals = async () => {
  if (!withdrawalsData.value.account) {
    showToast('Missing account info')
    return
  }

  const formData = new FormData()
  formData.append('account', withdrawalsData.value.account)
  if (user.value.payInUserId)
    formData.append('amount', withdrawalsData.value.amount)
  formData.append('withdrawType', withdrawalsData.value.withdrawType)
  if (withdrawalsData.value.file) formData.append('file', withdrawalsData.value.file)

  try {
    await applyWithdraw(formData)
    showToast('Submitted successfully')
    withdrawalsVisible.value = false
    withdrawalsData.value.file = null
    fileList.value = []
  } catch (error) {
    showToast(error || 'Submission failed')
  }
}

onMounted(() => {
  fetchUserData()
  fetchAutoReceiveSetting()
})
</script>

<style scoped>
.user-view {
  background: #f8f9fa;
  min-height: 100vh;
  padding-bottom: 80px;
}

.profile-header {
  background: linear-gradient(180deg, #ff9f00 0%, #ffb74d 100%);
  padding: 60px 20px 40px;
  border-bottom-left-radius: 40px;
  border-bottom-right-radius: 40px;
  text-align: center;
}

.avatar-section {
  position: relative;
  display: inline-block;
  margin-bottom: 24px;
}

.avatar-ring {
  padding: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
}

.profile-avatar {
  border: 4px solid #fff;
}

.user-id-badge {
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  color: #ff9f00;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.balance-card {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 24px;
  color: #fff;
  margin-top: 10px;
}

.balance-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.balance-amount {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 4px;
}

.currency-symbol {
  font-size: 18px;
  margin-right: 4px;
}

.balance-sub {
  font-size: 14px;
  opacity: 0.8;
  margin-bottom: 16px;
}

.deposit-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px;
  border-radius: 12px;
}

.action-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 24px 20px;
  margin-top: -20px;
}

.action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: #fff;
  padding: 16px 8px;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.action-item span {
  font-size: 11px;
  font-weight: 600;
  color: #444;
  text-align: center;
}

.action-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.action-icon-wrapper.records {
  background: #fff3e0;
  color: #ff9800;
}

.action-icon-wrapper.withdraw {
  background: #e3f2fd;
  color: #2196f3;
}

.action-icon-wrapper.kyc {
  background: #e8f5e9;
  color: #4caf50;
}

.action-icon-wrapper.password {
  background: #fce4ec;
  color: #e91e63;
}

.settings-card {
  margin: 0 20px 16px;
  padding: 16px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.auto-receive-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.auto-receive-text {
  flex: 1;
  min-width: 0;
}

.auto-receive-title {
  font-size: 16px;
  font-weight: 700;
  color: #1c1c1e;
  margin-bottom: 6px;
}

.auto-receive-desc {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #8e8e93;
}

.menu-list {
  padding: 0 20px;
}

.van-cell {
  background: #fff;
  margin-bottom: 8px;
  border-radius: 16px;
  padding: 16px;
}

.logout-cell {
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #ff3b30;
  font-weight: 700;
  padding: 16px;
  background: #fff;
  border-radius: 16px;
}

.withdraw-popup {
  max-height: 85vh;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #f2f2f7;
}

.popup-header h3 {
  margin: 0;
  font-size: 18px;
}

.popup-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #8e8e93;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.method-selector {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f2f2f7;
  padding: 14px 16px;
  border-radius: 12px;
  font-weight: 600;
}

.custom-field {
  padding: 10px 0;
  margin-bottom: 10px;
}

.upload-section {
  margin: 20px 0;
}

.upload-section label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #8e8e93;
  margin-bottom: 12px;
  text-transform: uppercase;
}

.uploader-placeholder {
  width: 100%;
  height: 120px;
  background: #f8f9fa;
  border: 2px dashed #d1d1d6;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8e8e93;
}

.uploader-placeholder p {
  margin-top: 8px;
  font-size: 12px;
}

.submit-btn {
  height: 52px;
  background: #ff9f00;
  border: none;
  font-weight: 700;
  font-size: 16px;
  margin-top: 20px;
}
</style>

